import type { QueryClient } from '@tanstack/react-query';
import { createIsomorphicFn } from '@tanstack/react-start';
import { posthog } from 'posthog-js';
import { shallow } from 'zustand/vanilla/shallow';
import { createAuthStore } from '#src/state/AuthStore.ts';

export const initStores = createIsomorphicFn()
  .client(({ queryClient }: { queryClient: QueryClient }) => {
    const useAuthStore = createAuthStore();

    useAuthStore.subscribe(
      state => state.session?.userId,
      (userId, previousUserId) => {
        if (userId) {
          posthog.identify(userId);
          return;
        }

        // Only clear after an actual logout or session loss. Do not clear
        // during initial startup when both values are undefined.
        if (previousUserId) {
          posthog.reset();
          queryClient.clear();
        }
      }
    );

    useAuthStore.subscribe(
      state => ({ locale: state.locale, userId: state.session?.userId }),
      (next, prev) => {
        if (!next.userId) {
          return;
        }

        const userChanged = next.userId !== prev.userId;
        const localeChanged = next.locale !== prev.locale;

        if (userChanged || localeChanged) {
          void useAuthStore.getState().fetchUser();
        }
      },
      { equalityFn: shallow }
    );

    useAuthStore.subscribe(
      state => state.user?.email,
      email => {
        if (email) {
          posthog.setPersonProperties({ email });
        }
      }
    );

    void useAuthStore.getState().fetchSession();

    return {
      useAuthStore
    };
  })
  .server(() => {
    const useAuthStore = createAuthStore();

    return {
      useAuthStore
    };
  });
